/**
 * Reactive feature-flag hook. Evaluates the named flag against the
 * current Vite build mode and the signed-in user's normalized email,
 * then applies demo-mode suppression.
 *
 * @agentic connections
 */

import { useEffect, useSyncExternalStore } from "react";
import {
  isFeatureEnabled,
  type AppEnv,
  type FlagName,
} from "@benchmark/core/feature-flags";
import { readFeatureOverride } from "@/utils/feature-flags-env";
import { isDemoModeActive, subscribeToDemoMode } from "@/utils/demo-mode-store";
import { trackFeatureFlagEvaluation } from "@/app/boot/telemetry";
import { useUserEmail } from "./useUserEmail";

/**
 * Vite resolves `import.meta.env.MODE` at build time to one of
 * `"development" | "test" | "staging" | "production"`. We capture it
 * once at module load because the value cannot change between renders.
 */
const APP_ENV = import.meta.env.MODE as AppEnv;

/**
 * Which flags keep evaluating normally while demo mode is on.
 *
 * Demo mode's job is that nothing flag-gated reaches a clinician audience
 * during a screen share, so the default is `false` — every flag is forced off,
 * including flags added after this was written. Typing this as
 * `Record<FlagName, boolean>` makes "does this survive demo mode?" a
 * compile-time question a new flag cannot skip, the same trick
 * `OVERRIDE_READERS` uses in `utils/feature-flags-env.ts`.
 * @agentic connections
 */
const SURVIVES_DEMO_MODE: Record<FlagName, boolean> = {
  // Demo mode must keep evaluating its own flag, or turning it on would hide
  // the control that turns it off.
  demoMode: true,
  // Inactivity auto-logout is a session-security control, not demo content. A
  // presentation toggle must never be able to disable it — an unattended
  // screen-shared session staying logged in is a worse outcome than a
  // clinician seeing the timeout warning.
  inactivityLogout: true,
  // Server-evaluated (`apps/api`); no Connections UI. Listed for exhaustiveness.
  dialpadCareTimeAutoCreate: false,
  // Flag-gated UI — suppressed. This is the point of demo mode.
  bandwidthDiagnostics: false,
  dialpadCallSelector: false,
};

/**
 * The flag's own value, ignoring demo mode: rule + `VITE_FEATURE_<NAME>`
 * override only.
 *
 * Reserved for ROUTE GUARDS. A guard swaps its whole subtree out when the flag
 * goes false, and demo mode can flip mid-session — so a suppressed guard would
 * unmount whatever the user was doing. `useFormProtection` guards navigation
 * and unload, not an unmount from a re-render, which means a half-written
 * Library document would be destroyed silently by a toggle click. Demo mode
 * therefore hides the way IN (nav entries) and everything rendered inline, but
 * never evicts a page already on screen.
 *
 * Every other call site wants {@link useFeatureFlag}.
 * @agentic connections
 */
export const useFeatureFlagRule = (name: FlagName): boolean => {
  const email = useUserEmail();

  const enabled = isFeatureEnabled(name, {
    environment: APP_ENV,
    email,
    override: readFeatureOverride(name),
  });

  // Forward the evaluation to Datadog RUM so errors/friction can be sliced by
  // rollout. Only fires when the resolved value changes; no-op without creds.
  useEffect(() => {
    trackFeatureFlagEvaluation(name, enabled);
  }, [name, enabled]);

  return enabled;
};

/**
 * Whether the named feature flag is on for the current user in the
 * current build environment. Threads an optional env-var override
 * (`VITE_FEATURE_<NAME>`) so ops can flip the flag at redeploy time, then
 * forces the flag off while demo mode is on unless it is exempt above.
 * @agentic connections
 */
export const useFeatureFlag = (name: FlagName): boolean => {
  const ruleEnabled = useFeatureFlagRule(name);
  const demoModeActive = useSyncExternalStore(
    subscribeToDemoMode,
    isDemoModeActive,
    isDemoModeActive,
  );

  // `name` is a closed `FlagName` union — the dynamic Record property
  // access is safe.
  // eslint-disable-next-line security/detect-object-injection
  const suppressed = demoModeActive && !SURVIVES_DEMO_MODE[name];

  return !suppressed && ruleEnabled;
};
